import Modal from "@mui/material/Modal";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import dayjs, { Dayjs } from "dayjs";
import { LegacyPlan } from "../../assets/interfaces/interfaces";
import { formatUsd } from "./bundleCatalogue";

export type ExpiryPhase = "none" | "early" | "countdown" | "expired";

export const COUNTDOWN_WINDOW_DAYS = 7;

export const deriveExpiryPhase = (
  migrateBy: string,
  now: Dayjs = dayjs(),
): { phase: Exclude<ExpiryPhase, "none">; daysLeft: number } => {
  const daysLeft = dayjs(migrateBy).startOf("day").diff(now.startOf("day"), "day");
  if (daysLeft < 0) return { phase: "expired", daysLeft: 0 };
  if (daysLeft <= COUNTDOWN_WINDOW_DAYS) return { phase: "countdown", daysLeft };
  return { phase: "early", daysLeft };
};

type ExpiryNudgeProps = {
  phase: ExpiryPhase;
  countdownDays: number;
  legacyPlan: LegacyPlan;
  onChooseBundles: () => void;
  onDefer: () => void;
};

export const ExpiryNudge = ({ phase, countdownDays, legacyPlan, onChooseBundles, onDefer }: ExpiryNudgeProps) => {
  const priceLabel = formatUsd(legacyPlan.price_monthly);

  if (phase === "expired") {
    return (
      <div className="flex flex-col items-center text-center">
        <div className="w-12 h-12 rounded-full bg-red-100 dark:bg-red-900/30 flex items-center justify-center mb-4">
          <AutoAwesomeIcon className="!w-6 !h-6 text-red-500" />
        </div>
        <Typography variant="h6/medium-500" className="!mb-2">
          Your TradeGPT
          {" "}
          {legacyPlan.type}
          {" "}
          has expired
        </Typography>
        <Typography variant="body2/regular-400" component="p" className="text-mui-black-60 dark:text-mui-white-56 !mb-6">
          Your
          {" "}
          {legacyPlan.type}
          {" "}
          plan has ended. You&apos;re now on the free tier with 5 prompts/day.
          Choose bundles to unlock Pro tools and get more prompts.
        </Typography>
        <Button
          variant="contained"
          fullWidth
          onClick={onChooseBundles}
          className="!bg-black dark:!bg-white !text-transform-none dark:text-mui-black-87 !mb-2"
        >
          Choose bundles
        </Button>
        <Button
          variant="text"
          fullWidth
          onClick={onDefer}
          className="!text-transform-none !text-mui-black-60 dark:!text-mui-white-56"
        >
          Maybe later
        </Button>
      </div>
    );
  }

  return (
    <div className="flex flex-col">
      {phase === "countdown" && (
        <div className="rounded-lg bg-amber-50 dark:bg-amber-900/20 px-3 py-2 mb-4">
          <Typography variant="body2/regular-400" className="!font-medium text-amber-700 dark:text-amber-400">
            {countdownDays}
            {" "}
            {countdownDays === 1 ? "day" : "days"}
            {" "}
            until your
            {" "}
            {legacyPlan.type}
            {" "}
            plan expires
          </Typography>
        </div>
      )}
      <div className="mb-6">
        <Typography variant="h6/medium-500" className="!mb-2">We&apos;re changing how plans work</Typography>
        <Typography variant="body2/regular-400" component="p" className="text-mui-black-60 dark:text-mui-white-56">
          Your
          {" "}
          {legacyPlan.type}
          {" "}
          plan (
          {priceLabel}
          /mo) is moving to bundles.
        </Typography>
      </div>
      <div className="rounded-lg bg-gray-50 dark:bg-white/[0.04] px-4 py-3 mb-6">
        <div className="grid grid-cols-2 gap-x-4 text-[11px] tracking-wide uppercase text-mui-black-38 dark:text-mui-white-38 mb-3">
          <span>Now</span>
          <span>With bundles</span>
        </div>
        <div className="grid grid-cols-2 gap-x-4 gap-y-2.5 text-sm">
          <span className="text-mui-black-38 dark:text-mui-white-38">Fixed tier pricing</span>
          <span className="font-medium">Pay per bundle</span>
          <span className="text-mui-black-38 dark:text-mui-white-38">All tools included</span>
          <span className="font-medium">Pick what you need</span>
          <span className="text-mui-black-38 dark:text-mui-white-38">Fixed prompt limit</span>
          <span className="font-medium">More bundles, more prompts</span>
        </div>
      </div>
      <Typography variant="caption" component="p" className="text-mui-black-60 dark:text-mui-white-56 !mb-5">
        Migrate by
        {" "}
        {dayjs(legacyPlan.migrate_by).format("MMMM D, YYYY")}
      </Typography>
      <Button
        variant="contained"
        fullWidth
        onClick={onChooseBundles}
        className="!bg-black dark:!bg-white !text-transform-none dark:text-mui-black-87 !mb-2"
      >
        Choose bundles
      </Button>
      <Button
        variant="text"
        fullWidth
        onClick={onDefer}
        className="!text-transform-none !text-mui-black-60 dark:!text-mui-white-56"
      >
        {phase === "early" ? "Remind me later" : "Don’t remind me again"}
      </Button>
    </div>
  );
};

type ExpiryNudgeModalProps = ExpiryNudgeProps;

const ExpiryNudgeModal = ({ phase, countdownDays, legacyPlan, onChooseBundles, onDefer }: ExpiryNudgeModalProps) => {
  if (phase === "none") return null;
  return (
    <Modal open onClose={onDefer} aria-labelledby="expiry-nudge-modal">
      <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 outline-none">
        <div className="shadow w-modal-mobile sm:w-modal-md bg-white dark:bg-background/paper-elevation-16 rounded-lg p-6 relative text-mui-black-87 dark:text-white">
          <IconButton size="small" onClick={onDefer} aria-label="Close" sx={{ position: "absolute", top: 12, right: 12 }}>
            <CloseIcon className="!fill-action-active !w-5 !h-5" />
          </IconButton>
          <ExpiryNudge
            phase={phase}
            countdownDays={countdownDays}
            legacyPlan={legacyPlan}
            onChooseBundles={onChooseBundles}
            onDefer={onDefer}
          />
        </div>
      </div>
    </Modal>
  );
};

export default ExpiryNudgeModal;
